import React, { useEffect } from 'react';

import useApi from '@/hooks/useApi';
import { useSlider } from '@/hooks/useSlider';

import { dispatchInteraction } from '@/services/requests/Interactions/helpers';
import { TInteractionCardClick } from '@/services/requests/Interactions/types';
import { TRecommendation } from '@/services/requests/Recommendation/types/template2';
import { sendTagEventFunction } from '@/services/requests/AmplitudeTag';

import { getVisibleCardsIdByElement } from '@/helpers/HtmlHelper/getVisibleCardsIdByElement';

import { Card } from './Card';
import { TSliderAreasProps } from './interface';
import { CARDS_SCROLL_BY, CARDS_VISIBLE } from './utils';
import { ButtonSlider } from './ButtonSlider';
import { useDetails } from './useDetails';
import { useTaxonomy } from './useTaxonomy';

export const Slider = ({
  data: { id, config, cards, type, tag_events },
}: TSliderAreasProps) => {
  const { Api } = useApi();
  const { cardsDetails, fetchDetails } = useDetails({
    cards,
    config,
  });
  const { images } = useTaxonomy({ cards });

  const {
    sliderState,
    moveSlider,
    moveSliderTo,
    containerRef,
    checkScrollLimit,
  } = useSlider(
    { id, cards } as unknown as TRecommendation,
    '',
    true,
    '1',
    'vitrine-template2__card',
  );

  function getCardsLeft(scrollSize: number) {
    return (
      sliderState.numberOfCards -
      CARDS_VISIBLE -
      (scrollSize / sliderState.cardsWidth) *
        (config.cards_scroll_by || CARDS_SCROLL_BY)
    );
  }

  async function handleCardClick(
    event: React.MouseEvent<HTMLAnchorElement>,
    cardId: string,
    index: number,
  ) {
    event.preventDefault();

    if (Array.isArray(tag_events) && tag_events.length > 0) {
      const selectEventTag = tag_events
        .filter(e => e.event === 'select_item')
        .find(e => e.index === cards[index].index);
      if (selectEventTag) {
        sendTagEventFunction(selectEventTag.data, selectEventTag.event);
      }
    }

    const redirectURL = event.currentTarget.href;

    await dispatchInteraction<TInteractionCardClick>(Api, 'card-click', {
      recommendationId: id,
      tabName: '',
      cardId,
      cardPosition: index + 1,
      cards: getVisibleCardsIdByElement(
        containerRef.current as HTMLElement,
        'card-click',
        '.vitrine-template2__card',
      ),
    }).finally(() => {
      window.location.href = redirectURL;
    });

    return true;
  }

  useEffect(() => {
    if (sliderState.scroll > 0) {
      const amountCardsLeft = getCardsLeft(sliderState.scroll);
      const startIndex =
        sliderState.numberOfCards - amountCardsLeft - config.cards_scroll_by;
      const endIndex =
        sliderState.numberOfCards -
        amountCardsLeft +
        config.cards_scroll_by -
        config.cards_scroll_by;

      fetchDetails(startIndex, endIndex);
    }
  }, [sliderState]);

  return (
    <div className="vitrine-template2__position">
      <ButtonSlider
        handleClick={e => moveSlider(e)}
        sliderScroll={sliderState.scroll}
        checkScrollLimit={checkScrollLimit}
      />
      <div
        ref={containerRef}
        className="vitrine-template2__cards"
        data-testid="cards2"
      >
        {cardsDetails.map((card, index) => (
          <Card
            // eslint-disable-next-line react/no-array-index-key
            key={`card-${card.id}-${index}`}
            data={card}
            onClick={e => handleCardClick(e, card.id, index)}
            image={images[index]}
            type={type}
          />
        ))}
      </div>
      <div className="vitrine-template2__slider-dots">
        {cards.map((card, i) => {
          return (
            <span
              role="button"
              key={`dot${card.id}`}
              className={`vitrine-template2__slider-dot${
                i + 1 === sliderState.current ? ' current' : ''
              }`}
              aria-hidden="true"
              tabIndex={0}
              onClick={() => moveSliderTo(i)}
            >
              {i + 1}
            </span>
          );
        })}
      </div>
    </div>
  );
};
